"use client";
import { useMutation, useQuery } from "@tanstack/react-query";

import api from "./axios.instance";

import { SubscriberPayload } from "@/types";

function BlogServices() {
  type BlogRequestFace = {
    page?: number;
    limit?: number;
    search?: string | null;
    author?: string | null;
    category?: string | null;
    from?: string | null;
    to?: string | null;
    onlyLatest?: boolean;
    excludeLatest?: boolean;
  };

  const useGetAllBlog = ({
    page = 1,
    limit = 12,
    search = null,
    author = null,
    category = null,
    from = null,
    to = null,
    onlyLatest = false,
    excludeLatest = false,
  }: BlogRequestFace) => {
    const fetchData = async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (search) params.append("q", search);
      if (author) params.append("author", author);
      if (category) params.append("category", category);
      if (from) params.append("from", from);
      if (to) params.append("to", to);
      if (onlyLatest) params.append("onlyLatest", "true");
      if (excludeLatest) params.append("excludeLatest", "true");

      const { data } = await api.get(`/blog?${params.toString()}`);

      return data || [];
    };

    return useQuery({
      queryFn: fetchData,
      queryKey: [
        "all_articles",
        page,
        limit,
        search,
        author,
        category,
        from,
        to,
        onlyLatest,
        excludeLatest,
      ],
      enabled: true,
      placeholderData: (prevData) => prevData,
    });
  };

  const useGetSingleBlog = (id: string) => {
    const fetchData = async () => {
      const data = await api.get(`/blog/${id}`);

      return data.data;
    };

    return useQuery({
      queryFn: () => fetchData(),
      queryKey: ["single_article", id],
      enabled: !!id,
    });
  };

  const useGetAllBlogCategory = () => {
    const fetchData = async () => {
      const data = await api.get("/blog/categories");

      return data.data || [];
    };

    return useQuery({
      queryFn: () => fetchData(),
      queryKey: ["all_categories"],
      enabled: true,
    });
  };

  const useBlogSubscriber = () => {
    return useMutation({
      mutationKey: ["blog-subscriber"],
      mutationFn: async (subscriberPayload: SubscriberPayload) => {
        return await api.post("/cotton-service/subscribers", subscriberPayload);
      },
      retry: false,
    });
  };

  const useIncrementBlogView = () => {
    return useMutation({
      mutationKey: ["increment-blog-view"],
      mutationFn: async (slug: string) => {
        return await api.post(`/blog/${slug}/view`);
      },
      retry: false,
    });
  };

  const useGetSimilarBlogs = (category?: string, slug?: string) => {
    const fetchData = async () => {
      const { data } = await api.get(
        `/blog/similar?category=${category}&slug=${slug}`,
      );

      return data || [];
    };

    return useQuery({
      queryFn: fetchData,
      queryKey: ["similar_blogs", category, slug],
      enabled: !!category && !!slug,
    });
  };

  const useGetBlogComments = (slug: string, page = 1, limit = 5) => {
    const fetchData = async () => {
      const data = await api.get(
        `/blog/${slug}/comments?page=${page}&limit=${limit}`,
      );

      return data.data;
    };

    return useQuery({
      queryFn: fetchData,
      queryKey: ["blog-comments", slug, page, limit],
      enabled: !!slug,
    });
  };

  const useAddBlogComment = () => {
    return useMutation({
      mutationKey: ["add_blog_comment"],

      mutationFn: async (payload: {
        slug: string;
        name: string;
        email: string;
        comment: string;
      }) => {
        return await api.post(`/blog/${payload.slug}/comments`, payload);
      },

      retry: false,
    });
  };

  return {
    useGetAllBlog,
    useGetAllBlogCategory,
    useGetSingleBlog,
    useBlogSubscriber,
    useIncrementBlogView,
    useGetSimilarBlogs,
    useGetBlogComments,
    useAddBlogComment,
  };
}
export default BlogServices;
